import type { CoachProfile } from '@efdigital/ll-addon-data/types/coaching/coach-profile';
import type { UserInfo } from '@efdigital/ll-admin-v2/data/builders/system/users';
import { queryCoachProfilesByUserIds } from '@efdigital/ll-admin-v2/data/builders/xceed/coach-profiles';
import { resolveCoachName } from '@efdigital/ll-admin-v2/utils/coaching/coach-display-name';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';

import {
  type AugmentedSuggestion,
  BaseUserPicker,
} from '../../pickers/user-picker.gts';

/**
 * Search-as-you-type coach filter, built on the shared BaseUserPicker rather
 * than a bespoke search UI. The base search covers every platform user, so
 * this augments each suggestion with its CoachProfile (if any) and drops the
 * ones without one — a coach's name is also resolved from CoachProfile
 * rather than the central user directory, which doesn't reliably carry a
 * title/email for coach accounts.
 */
export class CoachSearchPicker extends BaseUserPicker<
  CoachProfile | undefined
> {
  @service declare store: Store;

  knownProfiles: Record<string, { profile: CoachProfile | null }> = {};

  private async lookupProfiles(suggestions: UserInfo[]) {
    const unknownIds: string[] = [];
    for (const suggestion of suggestions) {
      if (!this.knownProfiles[suggestion.id]) {
        unknownIds.push(suggestion.id);
      }
    }

    if (unknownIds.length > 0) {
      const profiles = await this.store.request(
        queryCoachProfilesByUserIds(unknownIds)
      );
      for (const profile of profiles.content.data) {
        const user = profile.user;
        const index = unknownIds.indexOf(user);
        if (index !== -1) {
          unknownIds.splice(index, 1);
          this.knownProfiles[profile.user] = { profile };
        }
      }

      for (const id of unknownIds) {
        this.knownProfiles[id] = { profile: null };
      }
    }
  }

  async augmentSuggestions(
    suggestions: UserInfo[]
  ): Promise<AugmentedSuggestion<CoachProfile | undefined>[]> {
    // Trim suggestions to top 50 before resolving profiles.
    suggestions = suggestions.slice(0, 50);

    await this.lookupProfiles(suggestions);

    return suggestions.map((suggestion) => {
      const profile = this.knownProfiles[suggestion.id]?.profile ?? undefined;
      return {
        user: suggestion,
        meta: profile,
        disabled: false,
      };
    });
  }

  filterSuggestions(
    suggestions: AugmentedSuggestion<CoachProfile | undefined>[]
  ): AugmentedSuggestion<CoachProfile | undefined>[] {
    // Only real coaches — the base search covers every platform user.
    return suggestions.filter((suggestion) => Boolean(suggestion.meta));
  }

  resolveSelectedDisplay = async (coachUserId: string): Promise<string> => {
    return resolveCoachName(this.store, this.userLookup, coachUserId);
  };
}
